import { Tiptap } from "@/components/common/tiptap/Tiptap";
import Alert from "@/components/core/alert";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import Select from "@/components/core/forms/select";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { ScrollArea } from "@/components/core/scroller";
import { PdfViewer } from "@/components/pdf-viewer";
import caseRecordStatisticService from "@/features/case-record/api/services/case-record-statistic-service";
import caseCategoryService from "@/features/dashboard/api/services/case-category-service";
import caseTypeService from "@/features/dashboard/api/services/case-type-service";
import courtLevelService from "@/features/dashboard/api/services/court-level-service";
import courtTypeService from "@/features/dashboard/api/services/court-type-service";
import courtTypeStationService from "@/features/dashboard/api/services/court-type-station-service";
import provinceService from "@/features/dashboard/api/services/province-service";
import regionService from "@/features/dashboard/api/services/region-service";
import { IconAlertCircle, IconClockHour3, IconTemplate, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import Combobox from "./core/forms/combobox";

export default function ReportStatisticModal() {
	const { closeModal } = useModalControl();
	const form = useForm({
		defaultValues: {
			court_level: null,
			court_type: null,
			region_code: null,
			case_category: null,
			province_code: null,
			case_type: null,
		},
	});

	const [totalCount, setTotalCount] = useState(null);
	const [totalCourtTypes, setTotalCourtTypes] = useState([]);
	const [totalBranch, setTotalBranch] = useState([]);
	const [categories, setTotalCategory] = useState([]);
	const [caseTypes, setTotalCaseType] = useState([]);
	const [activeCategory, setActiveCategory] = useState({});
	const [activeBranch, setActiveBranch] = useState({});
	const [activeCourtType, setActiveCourtType] = useState({});
	const caseCategory = form.watch("case_category");
	const courtLevel = form.watch("court_level");
	const courtType = form.watch("court_type");
	const courtRegionCode = form.watch("region_code");
	const courtProvince = form.watch("province_code");

	const handleSubmit = async () => {
		setTotalCourtTypes([]);
		setTotalBranch([]);
		setTotalCategory([]);
		setTotalCaseType([]);
		const itemsQuery = await caseRecordStatisticService.list({
			"filter[court_level]": courtLevel,
			"filter[court_type_id]": courtType,
			"filter[case_category_id]": caseCategory,
		});
		setTotalCount(itemsQuery);
	};

	const openCourtTypeCount = async () => {
		setTotalBranch([]);
		setTotalCategory([]);
		setTotalCaseType([]);
		const itemsQuery = await caseRecordStatisticService.listByCourtType({
			"filter[court_level]": courtLevel,
			"filter[court_type_id]": courtType,
			"filter[case_category_id]": caseCategory,
		});
		setTotalCourtTypes(itemsQuery);
	};

	const openBranchCount = async (courtType) => {
		setActiveCourtType(courtType);
		setActiveBranch(null);
		setActiveCategory(null);
		setTotalCategory([]);
		setTotalCaseType([]);
		const itemsQuery = await caseRecordStatisticService.listByBranch({
			"filter[court_level]": courtLevel,
			"filter[court_type_id]": courtType.court_type_id,
			"filter[case_category_id]": caseCategory,
		});
		setTotalBranch(itemsQuery);
	};

	const openCategoryCount = async (branch) => {
		setActiveBranch(branch);
		setActiveCategory(null);
		setTotalCaseType([]);
		const itemsQuery = await caseRecordStatisticService.listByCategory({
			"filter[court_type_id]": branch.court_type_id,
			"filter[court_level]": courtLevel,
			"filter[branch_id]": branch.branch_id,
			"filter[case_category_id]": caseCategory,
		});
		setTotalCategory(itemsQuery);
	};

	const openCaseTypeCount = async (category) => {
		setActiveCategory(category);
		const itemsQuery = await caseRecordStatisticService.listByCaseType({
			"filter[court_level]": courtLevel,
			"filter[branch_id]": category.branch_id,
			"filter[case_category_id]": caseCategory ? caseCategory : category.case_category_id,
		});
		setTotalCaseType(itemsQuery);
	};

	return (
		<div className="flex flex-col w-full h-full">
			<Form {...form}>
				<form className="w-full h-full" onSubmit={form.handleSubmit(handleSubmit)}>
					<div className="flex flex-col w-full h-full">
						<PanelGroup className="flex flex-row grow w-full h-full" direction="horizontal">
							<Panel defaultSize={30} minSize={30} maxSize={30} className="flex flex-col h-full min-w-[420px] max-w-[420px]">
								<div className="flex shrink-0 flex-row items-center justify-between w-full h-[50px] px-4 border-b border-slate-200">
									<span className="text-base font-bold text-foreground-dark">Report Statistic</span>
									<button
										onClick={() => {
											closeModal();
										}}
										className="flex w-[20px] h-[20px] ring-offset-background transition-opacity outline-none border-none ring-0 text-foreground opacity-40 hover:opacity-100 disabled:pointer-events-none"
										type="button"
									>
										<IconX size={20} stroke={1.5} />
									</button>
								</div>

								<ScrollArea className="flex grow h-[calc(100svh - 120px)]">
									<div className="grid grid-cols-12 w-full p-4 gap-x-3 gap-y-2">
										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="court_type"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Court Type</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																placeholderValue={{
																	label: "Regional Trial Court",
																	value: "RTC",
																}}
																onValueChange={(value) => {
																	form.setValue("province_code", "");
																	form.setValue("city_code", "");

																	field.onChange(value);
																}}
																loadOptions={async (value: string) => {
																	const items = await courtTypeService.list({
																		"filter[search]": value,
																		"filter[court_level]": courtLevel,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.id,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="region_code"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Court Region</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																key={courtType}
																placeholderValue={{
																	label: "NCR",
																	value: "NCR",
																}}
																defaultValue={field.value}
																onValueChange={(value) => {
																	form.setValue("province_code", "");
																	form.setValue("city_code", "");

																	field.onChange(value);
																}}
																loadOptions={async (value: string) => {
																	const items = await regionService.list({
																		"filter[search]": value,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="province_code"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Court Province</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																placeholderValue={{
																	label: "Metro Manila",
																	value: "MM",
																}}
																key={courtRegionCode}
																disabled={!courtRegionCode}
																onValueChange={(value) => {
																	form.setValue("city_code", "");

																	field.onChange(value);
																}}
																loadOptions={async (value: string) => {
																	const items = await provinceService.list({
																		"filter[search]": value,
																		"filter[region_code]": courtRegionCode,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="court_station"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Court Station/Office</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																disabled={!courtProvince}
																key={courtProvince}
																defaultValue={field.value}
																placeholderValue={{
																	value: "RTC_MAK",
																	label: "MAKATI CITY",
																}}
																onValueChange={field.onChange}
																loadOptions={async (value: string) => {
																	const items = await courtTypeStationService.list({
																		"filter[search]": value,
																		"filter[province_code]": courtProvince,
																		"filter[court_type_code]": courtType,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="case_category"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Case Category</FormLabel>
															<FormDescription />
														</div>

														<FormGroup>
															<Combobox
																defaultValue={field.value}
																onValueChange={field.onChange}
																placeholderValue={{
																	label: "Civil Cases",
																	value: "CIVIL_CASE",
																}}
																loadOptions={async (value: string) => {
																	const items = await caseCategoryService.list({
																		"filter[search]": value,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.id,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="case_type"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Case Type</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																onValueChange={field.onChange}
																placeholderValue={{
																	label: "Collection of Sum of Money",
																	value: "930",
																}}
																loadOptions={async (value: string) => {
																	const items = await caseTypeService.list({
																		"filter[search]": value,
																		"filter[case_category_code]": caseCategory,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									</div>
								</ScrollArea>

								<div className="flex shrink-0 flex-row items-center w-full h-[70px] px-4 gap-2 mt-auto border-t border-slate-200">
									<Button
										className="w-full"
										variant="ghost"
										type="button"
										onClick={() => {
											closeModal();
										}}
									>
										Cancel
									</Button>
									<Button className="w-full">Filter</Button>
								</div>
							</Panel>

							<PanelResizeHandle className="group flex justify-center items-center w-[12px] border-r border-l border-border/50 bg-white">
								<div className="inline-flex w-[4px] h-[40px] rounded-sm bg-foreground/50 group-hover:bg-foreground/90" />
							</PanelResizeHandle>

							<Panel defaultSize={70} minSize={70} maxSize={70} className="flex flex-col shrink grow w-full h-full bg-slate-100">
								{totalCount !== null ? (
									<div className="bg-white h-full flex flex-col w-full border-b last:border-b-0 border-dashed border-border overflow-hidden">
										<ScrollArea className="flex grow h-[calc(100svh - 120px)]">
											<div className="w-full gap-4 p-4">
												{/* total Count */}
												<div className="grid grid-cols-12 w-full gap-4">
													<div className="col-span-4 cursor-pointer">
														<div className="flex flex-row w-full p-6 gap-3 rounded-md border border-border/50">
															<div className="inline-flex shrink-0 items-center justify-center w-[44px] h-[44px] mt-1 bg-primary-600 text-white rounded-full">
																<IconClockHour3 size={24} stroke={1.5} />
															</div>
															<div className="flex flex-col w-full">
																<button type="button" className="font-bold text-3xl text-foreground-dark" onClick={() => openCourtTypeCount()}>
																	{totalCount}
																</button>
																<span className="text-foreground/70">Total No. of Cases</span>
															</div>
														</div>
													</div>
												</div>
												{/* total Count @@ */}

												{/* Court Type */}
												<div className="grid grid-cols-12 w-full gap-4 mt-4">
													{totalCourtTypes.map((courtType, index) => (
														<div key={`court_type_${index}`} className="col-span-4 cursor-pointer">
															<div className="flex flex-row w-full p-6 gap-3 rounded-md border border-border/50">
																<div className="inline-flex shrink-0 items-center justify-center w-[44px] h-[44px] mt-1 bg-primary-600 text-white rounded-full">
																	<IconClockHour3 size={24} stroke={1.5} />
																</div>
																<div className="flex flex-col w-full">
																	<button type="button" className="font-bold text-3xl text-foreground-dark" onClick={() => openBranchCount(courtType)}>
																		{courtType.count}
																	</button>
																	<span className="text-foreground/70">{courtType.court_type.name}</span>
																</div>
															</div>
														</div>
													))}
												</div>
												{/* Court Type @@ */}

												{/* Branch */}
												<div className="grid grid-cols-12 w-full gap-4 mt-4">
													<div className="col-span-12 cursor-pointer">
														<h4>{activeCourtType?.court_type?.name}</h4>
													</div>
													{totalBranch.map((branch, index) => (
														<div key={`court_type_${index}`} className="col-span-4 cursor-pointer">
															<div className="flex flex-row w-full p-6 gap-3 rounded-md border border-border/50">
																<div className="inline-flex shrink-0 items-center justify-center w-[44px] h-[44px] mt-1 bg-primary-600 text-white rounded-full">
																	<IconClockHour3 size={24} stroke={1.5} />
																</div>
																<div className="flex flex-col w-full">
																	<button type="button" className="font-bold text-3xl text-foreground-dark" onClick={() => openCategoryCount(branch)}>
																		{branch.count}
																	</button>
																	<span className="text-foreground/70">{branch.branch.name}</span>
																</div>
															</div>
														</div>
													))}
												</div>
												{/* Branch @@ */}

												{/* Category */}
												<div className="grid grid-cols-12 w-full gap-4 mt-4">
													<div className="col-span-12 cursor-pointer">
														<h4>{activeBranch?.branch?.name}</h4>
													</div>
													{categories.map((category, index) => (
														<div key={`court_type_${index}`} className="col-span-4 cursor-pointer">
															<div className="flex flex-row w-full p-6 gap-3 rounded-md border border-border/50">
																<div className="inline-flex shrink-0 items-center justify-center w-[44px] h-[44px] mt-1 bg-primary-600 text-white rounded-full">
																	<IconClockHour3 size={24} stroke={1.5} />
																</div>
																<div className="flex flex-col w-full">
																	<button type="button" className="font-bold text-3xl text-foreground-dark" onClick={() => openCaseTypeCount(category)}>
																		{category.count}
																	</button>
																	<span className="text-foreground/70">{category.category?.name}</span>
																</div>
															</div>
														</div>
													))}
												</div>
												{/* Category @@ */}

												{/* CaseType */}
												<div className="grid grid-cols-12 w-full gap-4 mt-4">
													<div className="col-span-12 cursor-pointer">
														<h4>{activeCategory?.category?.name}</h4>
													</div>
													{caseTypes.map((caseType, index) => (
														<div key={`court_type_${index}`} className="col-span-4 cursor-pointer">
															<div className="flex flex-row w-full p-6 gap-3 rounded-md border border-border/50">
																<div className="inline-flex shrink-0 items-center justify-center w-[44px] h-[44px] mt-1 bg-primary-600 text-white rounded-full">
																	<IconClockHour3 size={24} stroke={1.5} />
																</div>
																<div className="flex flex-col w-full">
																	<button
																		type="button"
																		className="font-bold text-3xl text-foreground-dark"
																		onClick={() => openCaseTypeCount(caseType.case_category)}
																	>
																		{caseType.count}
																	</button>
																	<span className="text-foreground/70">{caseType.case_type?.name}</span>
																</div>
															</div>
														</div>
													))}
												</div>
												{/* CaseType @@ */}
											</div>
										</ScrollArea>
									</div>
								) : (
									<Blanket className="my-auto self-center" width="sm">
										<BlanketImage icon={<IconTemplate size={24} stroke={1.5} />} />
										<BlanketInfo title="No Selected Filter" message="Please select a filter to display the relevant data." />
									</Blanket>
								)}
							</Panel>
						</PanelGroup>
					</div>
				</form>
			</Form>
		</div>
	);
}
